import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import { InterviewProblem } from "../../models/problems";
import { ProblemAttempt } from "../../store/problemAttempts";
import { COLOR_PRIMARY, COLOR_TEXT_MUTED } from "../../styles/colors";
import { DifficultyChip, ProblemDetails } from "./ProblemDetails";

interface ProblemCardProps {
  problem: InterviewProblem;
  expanded: boolean;
  onToggle: () => void;
  onStart: (problem: InterviewProblem) => void;
  /** The player's saved attempt at this problem, if any. */
  attempt?: ProblemAttempt;
  onRestart?: (problem: InterviewProblem) => void;
}

export const AttemptedChip: React.FC = () => (
  <Chip
    icon={<CheckCircleIcon />}
    label="Attempted"
    size="small"
    color="primary"
    data-testid="attempted-chip"
    sx={{ fontWeight: 700, backgroundColor: COLOR_PRIMARY }}
  />
);

const formatLastWorkedOn = (iso: string): string | null => {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const ProblemCard: React.FC<ProblemCardProps> = ({ problem, expanded, onToggle, onStart, attempt, onRestart }) => {
  const lastWorkedOn = attempt ? formatLastWorkedOn(attempt.updatedAt) : null;
  return (
    <Card variant="outlined" data-testid={`problem-card-${problem.id}`} sx={{ borderRadius: "12px" }}>
      <CardActionArea onClick={onToggle} aria-expanded={expanded} sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLOR_PRIMARY, flexGrow: 1 }}>
            {problem.title}
          </Typography>
          {attempt && <AttemptedChip />}
          <DifficultyChip difficulty={problem.difficulty} />
          <ExpandMoreIcon
            sx={{ color: COLOR_TEXT_MUTED, transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_MUTED, mb: 1 }}>
          {problem.summary}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {problem.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
          ))}
        </Box>
      </CardActionArea>
      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <ProblemDetails problem={problem} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 1 }}>
            {lastWorkedOn && (
              <Typography variant="caption" sx={{ color: COLOR_TEXT_MUTED, flexGrow: 1 }} data-testid="last-worked-on">
                Last worked on {lastWorkedOn}
              </Typography>
            )}
            {attempt && onRestart && (
              <Button
                startIcon={<ReplayIcon />}
                onClick={() => onRestart(problem)}
                data-testid="restart-problem-btn"
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Start over
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => onStart(problem)}
              data-testid="start-problem-btn"
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {attempt ? "Resume problem" : "Start problem"}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
};
